import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Lock, Crown } from "lucide-react";

/**
 * TierUpgradeTimeline
 * ───────────────────
 * Reads `GET /api/billing/tier` + `GET /api/billing/packages` and renders
 * the customer's lifecycle through ArmIQ's four maturity tiers as a
 * horizontal progress strip. Each step shows:
 *   • tier name + tagline
 *   • dollar price (so the upgrade cost is never a surprise)
 *   • current cap (athletes / coaches / teams)
 *   • a status pill — ACTIVE / NEXT / FUTURE / LOCKED
 *
 * Sits on the Settings page so every coach sees their growth runway and
 * the dollar-delta to the next step. Turns the cap-reached 402 friction
 * into an *expected milestone* instead of a surprise paywall.
 */
const ORDER = ["athlete", "team", "performance", "enterprise"];
const PRICE_FOR = {
  athlete:     { display: "$149 / yr",    package: "athlete_annual" },
  team:        { display: "$1,495 / yr",  package: "team" },
  performance: { display: "$2,995 / yr",  package: "performance" },
  enterprise:  { display: "Call for pricing", package: "enterprise" },
};

export default function TierUpgradeTimeline() {
  const [tierInfo, setTierInfo] = useState(null);
  const [tiers, setTiers] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([
          api.get("/billing/tier"),
          api.get("/billing/packages"),
        ]);
        setTierInfo(t.data);
        setTiers(p.data.tiers);
      } catch { /* hidden if endpoints unavailable */ }
    })();
  }, []);

  if (!tierInfo || !tiers) return null;

  const currentIdx = ORDER.indexOf(tierInfo.tier);
  return (
    <div className="rounded-2xl ink-card p-6 md:p-7" data-testid="tier-upgrade-timeline">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/40">Your growth runway</div>
          <h3 className="font-display font-black text-2xl tracking-tighter text-white mt-1">
            {tierInfo.meta?.name} tier · <span className="text-white/55">{tierInfo.meta?.tagline}</span>
          </h3>
        </div>
        <Link to="/pricing" className="text-xs tracking-[0.15em] uppercase font-bold text-white/55 hover:text-white">
          Compare all tiers →
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {ORDER.map((id, i) => {
          const tier = tiers[id];
          const active = i === currentIdx;
          const isNext = i === currentIdx + 1;
          const isFuture = i > currentIdx + 1;
          const isPast = i < currentIdx;
          const status = active ? "ACTIVE" : isNext ? "NEXT" : isPast ? "EARLIER" : "FUTURE";
          const palette = active
            ? { bg: "rgba(217,4,41,0.12)", border: "var(--brand-secondary)", chip: "var(--brand-secondary)", chipFg: "#fff" }
            : isNext
              ? { bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.45)", chip: "#F59E0B", chipFg: "#0A1424" }
              : isPast
                ? { bg: "rgba(255,255,255,0.02)", border: "rgba(255,255,255,0.08)", chip: "#94A3B8", chipFg: "#0A1424" }
                : { bg: "rgba(255,255,255,0.02)", border: "rgba(255,255,255,0.08)", chip: "#475569", chipFg: "#cbd5e1" };
          const caps = [
            tier.max_athletes == null ? "Unlimited athletes" : `${tier.max_athletes} athletes`,
            tier.max_coaches == null ? "Unlimited coaches" : `${tier.max_coaches} coaches`,
            tier.api_access ? "API access" : "Standard tools",
            tier.white_label ? "White-label" : null,
          ].filter(Boolean);
          return (
            <div
              key={id}
              data-testid={`tier-timeline-${id}`}
              className="rounded-xl border p-4 flex flex-col"
              style={{ backgroundColor: palette.bg, borderColor: palette.border }}
            >
              <div className="flex items-center justify-between">
                <span className="text-[9px] tracking-[0.25em] uppercase font-bold px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: palette.chip, color: palette.chipFg }}>
                  {status}
                </span>
                {id === "enterprise" && <Crown className="h-3.5 w-3.5" style={{ color: "#FFD700" }} />}
                {isPast && <Check className="h-4 w-4" style={{ color: "#16A34A" }} />}
              </div>
              <div className="font-display font-black text-xl text-white mt-2">{tier.name}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/45 mt-0.5">{tier.tagline}</div>
              <div className="mt-3 font-mono-num text-base font-bold text-white">{PRICE_FOR[id]?.display}</div>
              <ul className="mt-3 space-y-1 flex-1">
                {caps.map((c) => (
                  <li key={c} className="text-[11px] text-white/65 flex items-start gap-1.5">
                    <Check className="h-3 w-3 mt-0.5 shrink-0" style={{ color: active ? "var(--brand-secondary)" : "#475569" }} />
                    {c}
                  </li>
                ))}
              </ul>
              {isNext && (
                <Link to={id === "enterprise" ? "/enterprise/contact" : "/pricing"} className="mt-3">
                  <Button size="sm" className="w-full text-white red-glow font-bold rounded-full text-xs h-8" style={{ backgroundColor: palette.chip, color: palette.chipFg }} data-testid={`tier-timeline-upgrade-${id}`}>
                    Upgrade <ArrowRight className="h-3 w-3 ml-1.5" />
                  </Button>
                </Link>
              )}
              {isFuture && !isPast && (
                <div className="mt-3 text-[10px] flex items-center gap-1 text-white/35">
                  <Lock className="h-3 w-3" /> Unlocks after {ORDER[i - 1] && tiers[ORDER[i - 1]]?.name}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Live usage strip — shows the caps closest to the wire */}
      {tierInfo.limits?.athletes && (
        <div className="mt-5 grid grid-cols-3 gap-3" data-testid="tier-timeline-usage">
          {["athletes", "coaches", "teams"].map((k) => {
            const lim = tierInfo.limits[k];
            if (!lim) return null;
            const pct = lim.cap ? Math.min(100, Math.round((lim.current / lim.cap) * 100)) : 0;
            return (
              <div key={k} className="rounded-lg p-3" style={{ backgroundColor: "var(--ink-1)" }}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/45">{k}</span>
                  <span className="text-xs font-mono-num text-white">
                    {lim.current} / {lim.cap == null ? "∞" : lim.cap}
                  </span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: lim.over_limit ? "var(--brand-secondary)" : lim.near_limit ? "#F59E0B" : "#16A34A" }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
